import { 
  ChangeDetectionStrategy, 
  Component, 
  ElementRef, 
  ViewChild, 
  EventEmitter, 
  Output, 
  ChangeDetectorRef, 
  AfterViewInit, 
  OnDestroy, 
  OnInit,
  Input,
  AfterViewChecked,
  Injector
} from "@angular/core";
import { hasClientSmallScreen } from "environments/environment-helpers";
import { DestroyGuard, isElementScrollable } from "services/browser-utils";

@Component({
  selector: 'cs-scrollable',
  templateUrl: 'cs-scrollable.html',
  host: { 'class': 'scrollable' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsScrollableComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('scrollBar', { static: true }) scrollBar: ElementRef;
  @ViewChild('scrollArea', { static: true }) scrollArea: ElementRef;
  @ViewChild('scrollBarHoverArea', { static: true }) scrollBarHoverArea: ElementRef;

  @Input() scrollAreaClass: string = '';
  @Input() disableScrollbar: boolean = false;
  @Output() scroll = new EventEmitter<Event>();

  private destroyGuard = new DestroyGuard();
  private hideTimeout: any;
  private hideDelay: number = 2000;
  private expandedTimeout: any;
  private expandedDelay: number = 300;
  
  scrollBarHovered: boolean = false;
  scrollBarHoverAreaHovered: boolean = false;
  
  private isDragging: boolean = false;
  private startY: number = 0;
  private startScrollTop: number = 0;
  private lastScrollableState: boolean = null;
  
  private resizeObserver: ResizeObserver;
  private mutationObserver: MutationObserver;
  private mutationDebounceTimer: any;

  // Cache DOM elements to avoid repeated queries
  private scrollBarElement: HTMLElement;
  private scrollAreaElement: HTMLElement;
  private scrollBarHoverAreaElement: HTMLElement;

  // Store bound functions to properly remove listeners
  private boundMouseMove: (e: MouseEvent) => void;
  private boundMouseUp: (e: MouseEvent) => void;
  private boundTouchMove: (e: TouchEvent) => void;
  private boundTouchEnd: (e: TouchEvent) => void;
  private boundHostMouseMove: (e: MouseEvent) => void;
  private boundHostMouseLeave: () => void;

  constructor(
    public elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private injector: Injector
  ) {
    // Bind functions once to avoid creating new functions on each call
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
    this.boundTouchMove = this.onTouchMove.bind(this);
    this.boundTouchEnd = this.onTouchEnd.bind(this);
    this.boundHostMouseMove = this.onHostMouseMove.bind(this);
    this.boundHostMouseLeave = this.onHostMouseLeave.bind(this);

    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.refreshScrollbar());
    });
  }

  async ngOnInit() {
    if (this.disableScrollbar) return;

    // Cache DOM elements
    this.scrollBarElement = this.scrollBar.nativeElement as HTMLElement;

    this.scrollBarHoverAreaElement = this.scrollBarHoverArea.nativeElement as HTMLElement;

    // Add document-level event listeners
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
    document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundTouchEnd);

    // Add component-level event listeners.
    //
    // "the pointer is near the scroll bar" is worked out from the pointer's position on the
    // whole component, NOT from mouseenter/mouseleave on the strip that marks that area out.
    // The strip ignores the pointer entirely (df-770673c812a9): while it did not, it sat above
    // the content and silently swallowed every click landing in it.
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    hostElement.addEventListener('mousemove', this.boundHostMouseMove);
    hostElement.addEventListener('mouseleave', this.boundHostMouseLeave);

    this.scrollBarElement.addEventListener('mouseenter', this.onScrollBarMouseEnter.bind(this));
    this.scrollBarElement.addEventListener('mouseleave', this.onScrollBarMouseLeave.bind(this));

    // the wait now returns when this component is destroyed instead of hanging
    // (df-3f0eaff27ac6), so the scroll listener and the two observers below have to be
    // skipped in that case — ngOnDestroy has already run, so nothing would take them
    // away again, and the mutation debounce timer they start would outlive the component
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    // Check both scrollArea and its first child for scrollability
    const scrollAreaEl = this.scrollArea.nativeElement as HTMLElement;
    const firstChild = scrollAreaEl.firstElementChild as HTMLElement;

    // Pick the scrollable element, preferring first child if it's scrollable
    if (firstChild && isElementScrollable(firstChild)) {
      this.scrollAreaElement = firstChild;
    } else {
      this.scrollAreaElement = scrollAreaEl;
    }

    this.scrollAreaElement.addEventListener('scroll', this.handleScroll.bind(this));
    this.scrollAreaElement.style.setProperty('scrollbar-width', 'none');

    const child = this.scrollAreaElement.firstElementChild as HTMLElement;
    if (child) {
      this.resizeObserver.observe(child);
    }

    this.adjustVisibility();

    this.mutationObserver = new MutationObserver(() => {
      this.destroyGuard.cancelTimeout(this.mutationDebounceTimer);
      this.mutationDebounceTimer = this.destroyGuard.safeTimeout(() => {
        requestAnimationFrame(() => this.refreshScrollbar());
      }, 50);
    });

    this.mutationObserver.observe(this.scrollAreaElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeOldValue: false
    });

    // Don't show scrollbar initially - wait for first interaction or scroll
    // this.resetHideTimeout(); // Removed to keep scrollbar hidden initially
  }

  ngAfterViewInit() {
    if (this.disableScrollbar) return;

    // Immediate initial calculation
    this.refreshScrollbar();

    // Schedule additional calculation as fallback
    requestAnimationFrame(() => {
      this.computeScrollPositionAndSize();
      this.cd.markForCheck();
    });
  }

  adjustVisibility() {
    if (this.scrollBarHoverAreaElement) {
      const isScrollable = this.isScrollable();
      if (this.lastScrollableState !== isScrollable) {
        this.scrollBarHoverAreaElement.hidden = !isScrollable;
        this.lastScrollableState = isScrollable;

        // Hide scrollbar when content becomes non-scrollable
        if (!isScrollable && this.scrollBarElement) {
          this.scrollBarElement.style.opacity = '0';
        }
      }
    }
  }

  ngAfterViewChecked() {
    if (this.disableScrollbar) return;

    this.adjustVisibility();
  }

  ngOnDestroy() {
    // also cancels every pending timer below: they are all started through the guard
    this.destroyGuard.destroy();
    // Remove document-level event listeners to prevent memory leaks
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
    document.removeEventListener('touchmove', this.boundTouchMove);
    document.removeEventListener('touchend', this.boundTouchEnd);

    // Disconnect observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Re-measures the scroll bar after the content changed size, changed, or was first laid
   * out, and on a screen that is not small shows it (starting the fade-out countdown) when
   * the content overflows.
   */
  private refreshScrollbar() {
    this.computeScrollPositionAndSize();

    // if this is not a small screen and the content overflows, show the bar and start the hide timer
    if (!hasClientSmallScreen() && this.isScrollable()) {
      this.showScrollbar();
      this.resetHideTimeout();
    }
    this.cd.markForCheck();
  }

  isScrollable(): boolean {
    if (!this.scrollAreaElement) {
      return false;
    }
    return isElementScrollable(this.scrollAreaElement);
  }

  /**
   * The element a page should use as the root of an IntersectionObserver over this
   * area's content: the first child when it overflows, otherwise the area itself.
   * Worked out afresh on every call, because the child that overflows can change as
   * the content loads. Pages ask for it here instead of querying this component's
   * DOM, so the structure below stays private.
   *
   * It compares heights only. It does NOT use isElementScrollable(), the stricter
   * test (it also requires overflow-y auto or scroll) that ngAfterViewInit uses to
   * pick the element this component listens on. The two can disagree; this one keeps
   * the choice the changelog and task calendar pages always made.
   */
  getScrollRootElement(): HTMLElement | null {
    const scrollAreaEl = this.scrollArea?.nativeElement as HTMLElement | undefined;
    if (!scrollAreaEl) return null;
    const firstChild = scrollAreaEl.firstElementChild as HTMLElement | null;
    if (firstChild && firstChild.scrollHeight > firstChild.clientHeight) {
      return firstChild;
    }
    return scrollAreaEl;
  }

  resetToTop() {
    if (!this.scrollAreaElement) return;
    this.scrollAreaElement.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior,
    });
  }

  private retryCount = 0;
  private maxRetries = 10;

  computeScrollPositionAndSize() {
    // if the component is gone, a retry scheduled below has nothing left to measure
    if (this.disableScrollbar || this.destroyGuard.isDestroyed) return;

    if (!this.scrollBarElement || !this.scrollAreaElement) {
      return;
    }

    if (!this.scrollAreaElement.offsetHeight || !this.scrollAreaElement.scrollHeight) {
      // Use a faster retry mechanism for initial calculations
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        // Use setTimeout with 0 delay for faster retries initially
        if (this.retryCount <= 3) {
          this.destroyGuard.safeTimeout(() => this.computeScrollPositionAndSize(), 0);
        } else {
          requestAnimationFrame(() => this.computeScrollPositionAndSize());
        }
      }
      return;
    }

    // Reset retry count on successful calculation
    this.retryCount = 0;

    const fullSize = this.scrollAreaElement.scrollHeight;
    const viewSize = this.scrollAreaElement.offsetHeight;

    // If content is not scrollable, ensure scrollbar is hidden
    if (viewSize >= fullSize) {
      this.scrollBarElement.style.height = '0%';
      this.scrollBarElement.style.transform = 'translateY(0px)';
      this.scrollBarElement.style.opacity = '0';
      return;
    }

    const scrollTop = this.scrollAreaElement.scrollTop;
    const percentSize = Math.min(Math.max(viewSize / fullSize, 0), 1);
    this.scrollBarElement.style.height = `${percentSize * 100}%`;
    const scrollBarPosition = (scrollTop / fullSize) * viewSize;
    this.scrollBarElement.style.transform = `translateY(${scrollBarPosition}px)`;
  }

  handleScroll(event: Event) {
    this.scroll.emit(event);
    this.computeScrollPositionAndSize();
    this.showScrollbar();
    this.resetHideTimeout();
  }

  /**
   * Decides whether the pointer counts as "near the scroll bar" from where it is on the
   * component, and runs the enter/leave logic on each change.
   *
   * The invisible strip down the right-hand edge marks that area out but ignores the pointer,
   * so it cannot report the pointer arriving itself. Its rectangle is still what says where
   * the area IS, which keeps the width written in one place -- the stylesheet, where a media
   * query makes it wider on a device with a mouse. A hidden strip has an empty rectangle, so a
   * screen with nothing to scroll never reports the pointer as near.
   */
  private onHostMouseMove(event: MouseEvent) {
    if (!this.scrollBarHoverAreaElement) return;

    const area = this.scrollBarHoverAreaElement.getBoundingClientRect();
    const pointerIsNearScrollBar = area.width > 0
      && event.clientX >= area.left && event.clientX <= area.right
      && event.clientY >= area.top && event.clientY <= area.bottom;

    // if nothing changed, there is nothing to do -- this runs on every pointer move
    if (pointerIsNearScrollBar === this.scrollBarHoverAreaHovered) return;

    if (pointerIsNearScrollBar) {
      this.onScrollBarHoverAreaMouseEnter();
    } else {
      this.onScrollBarHoverAreaMouseLeave();
    }
  }

  private onHostMouseLeave() {
    // the pointer left the component altogether, so it cannot still be near the scroll bar.
    // Without this, leaving sideways or downwards out of the strip would never be noticed
    if (this.scrollBarHoverAreaHovered) {
      this.onScrollBarHoverAreaMouseLeave();
    }
  }

  onScrollBarMouseEnter() {
    this.scrollBarHovered = true;
  }

  onScrollBarMouseLeave() {
    this.scrollBarHovered = false;
  }

  onScrollBarHoverAreaMouseEnter() {
    this.scrollBarHoverAreaHovered = true;
    if (this.scrollBarHoverAreaElement) {
      this.scrollBarHoverAreaElement.classList.add('active');
    }
    this.showScrollbar();
    this.clearHideTimeout();
    this.clearExpandedTimeout();
  }

  onScrollBarHoverAreaMouseLeave() {
    this.scrollBarHoverAreaHovered = false;
    this.resetHideTimeout();
    this.resetExpandedTimeout();
  }

  onMouseDown(event: MouseEvent) {
    if (!this.scrollAreaElement) return;
    this.beginDrag(event.clientY);
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    this.handleDrag(event.clientY);
  }

  onMouseUp(event: MouseEvent) {
    void event;
    if (!this.isDragging) return;

    this.endDrag();
    // if the pointer is still near the bar (or on it), keep it shown (or wide) until it leaves
    if (!this.scrollBarHoverAreaHovered) {
      this.resetHideTimeout();
    }
    if (!this.scrollBarHovered) {
      this.resetExpandedTimeout();
    }
  }

  onTouchStart(event: TouchEvent) {
    if (!this.scrollAreaElement) return;
    this.beginDrag(event.touches[0].clientY);
    event.preventDefault();
  }

  private onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;
    this.handleDrag(event.touches[0].clientY);
    event.preventDefault();
  }

  /**
   * Unlike onMouseUp, this has no "is a drag in progress" or "is the pointer hovering"
   * check, and that difference is deliberate. A touch has no hover, so there is nothing
   * to keep the bar shown for. And this listener sits on the whole document, so it fires
   * for every tap anywhere. Restarting the fade countdowns then is harmless: it shows
   * nothing, it only starts the 2-second fade (and the narrowing) over again.
   */
  private onTouchEnd(event: TouchEvent) {
    void event;
    this.endDrag();
    this.resetHideTimeout();
    this.resetExpandedTimeout();
  }

  /** Starts a drag from this y: remembers where it began, marks the bar active, shows it and stops both countdowns. */
  private beginDrag(startY: number) {
    this.isDragging = true;
    this.startY = startY;
    this.startScrollTop = this.scrollAreaElement.scrollTop;

    if (this.scrollBarHoverAreaElement) {
      this.scrollBarHoverAreaElement.classList.add('active');
    }
    if (this.scrollBarElement) {
      this.scrollBarElement.classList.add('active');
    }

    this.showScrollbar();
    this.clearHideTimeout();
    this.clearExpandedTimeout();
  }

  /** Ends a drag. The caller decides whether to restart the countdowns, because mouse and touch differ there. */
  private endDrag() {
    this.isDragging = false;
    if (this.scrollBarElement) {
      this.scrollBarElement.classList.remove('active');
    }
  }

  private handleDrag(currentY: number) {
    if (!this.scrollAreaElement || !this.scrollBarElement) return;
    
    const deltaY = currentY - this.startY;
    const scrollableHeight = this.scrollAreaElement.scrollHeight - this.scrollAreaElement.clientHeight;
    const scrollBarHeight = this.scrollBarElement.offsetHeight;
    const scrollBarMaxY = this.scrollAreaElement.clientHeight - scrollBarHeight;

    const scrollPercent = deltaY / scrollBarMaxY;
    const newScrollTop = this.startScrollTop + scrollPercent * scrollableHeight;

    this.scrollAreaElement.scrollTop = newScrollTop;
    this.showScrollbar();
    this.clearHideTimeout();
  }

  private showScrollbar() {
    if (this.disableScrollbar || !this.scrollBarElement) return;
    
    // Only show if content is scrollable
    if (this.isScrollable()) {
      // Remove hiding class for instant show
      this.scrollBarElement.classList.remove('hiding');
      this.scrollBarElement.style.opacity = '1';
    }
  }

  private hideScrollbar() {
    if (!this.scrollBarElement) return;

    if (hasClientSmallScreen() || !this.isScrollable()) {
      // Add hiding class for smooth fade out
      this.scrollBarElement.classList.add('hiding');
      this.scrollBarElement.style.opacity = '0';
    }
  }

  private clearHideTimeout() {
    if (this.hideTimeout) {
      this.destroyGuard.cancelTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private resetHideTimeout() {
    this.clearHideTimeout();
    this.hideTimeout = this.destroyGuard.safeTimeout(() => {
      if (this.isDragging) {
        return;
      }
      this.hideScrollbar();
    }, this.hideDelay);
  }

  private resetExpandedTimeout() {
    this.clearExpandedTimeout();
    this.expandedTimeout = this.destroyGuard.safeTimeout(() => {
      if (this.isDragging) {
        return;
      }
      if (this.scrollBarHoverAreaElement) {
        this.scrollBarHoverAreaElement.classList.remove('active');
      }
    }, this.expandedDelay);
  }

  private clearExpandedTimeout() {
    if (this.expandedTimeout) {
      this.destroyGuard.cancelTimeout(this.expandedTimeout);
      this.expandedTimeout = null;
    }
  }
}